import React, { useState } from 'react';
import { Heart, Target, Lightbulb, Globe, Users, Briefcase, Shield } from 'lucide-react';

// Sample images - replace with your actual imports
const missionImage = "/images/mission.jpg";
const visionImage = "/images/vision.jpg";
const valuesImage = "/images/value.jpg";

// Image imports for the hover boxes
const culturalImage = "/images/culture.jpg";
const commercialImage = "/images/commercial.jpg";
const sharedImage = "/images/shared.jpg";
const economicImage = "/images/economic.jpg";

// TabPanel component for accessibility
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`organization-tabpanel-${index}`}
      aria-labelledby={`organization-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

function OrganizationTabs() {
  const [value, setValue] = useState(0);
  const [animate, setAnimate] = useState(false);

  // Define the sections data
  const sections = [
    {
      key: 'mission',
      title: 'Our Mission',
      color: 'blue',
      image: missionImage,
      content: 'To facilitate and strengthen economic ties between India and Europe through sustainable partnerships, cultural exchanges, and innovative collaboration frameworks that benefit both regions.',
      boxes: [
        {
          title: 'Cultural',
          subtitle: 'Exchange',
          color: 'purple',
          icon: <Globe size={24} />,
          image: culturalImage
        },
        {
          title: 'Commercial',
          subtitle: 'Collaboration',
          color: 'green',
          icon: <Briefcase size={24} />,
          image: commercialImage
        },
        {
          title: 'Shared',
          subtitle: 'Values',
          color: 'yellow',
          icon: <Heart size={24} />,
          image: sharedImage
        },
        {
          title: 'Economic',
          subtitle: 'Opportunities',
          color: 'blue',
          icon: <Target size={24} />,
          image: economicImage
        }
      ]
    },
    {
      key: 'vision',
      title: 'Our Vision',
      color: 'green',
      image: visionImage,
      content: 'To become the premier platform bridging economic opportunities between India and Europe, creating a robust ecosystem of shared prosperity, intercultural understanding, and technological innovation.',
    },
    {
      key: 'values',
      title: 'Our Values',
      color: 'purple',
      image: valuesImage,
      content: [
        {
          name: 'Integrity',
          icon: <Shield className="text-purple-500" size={20} />,
          desc: 'Upholding the highest standards of transparency and ethical practices in all partnerships and engagements.'
        },
        {
          name: 'Collaboration',
          icon: <Users className="text-blue-500" size={20} />,
          desc: 'Fostering inclusive teamwork and partnerships that leverage diverse perspectives and talents.'
        },
        {
          name: 'Innovation',
          icon: <Lightbulb className="text-yellow-500" size={20} />,
          desc: 'Continuously pursuing creative solutions and forward-thinking approaches to economic challenges.'
        },
        {
          name: 'Sustainability',
          icon: <Globe className="text-green-500" size={20} />,
          desc: 'Ensuring that all initiatives contribute to long-term environmental, social, and economic well-being.'
        }
      ]
    }
  ];

  // Handle tab change with animation
  const handleChange = (newValue) => {
    setAnimate(true);
    setTimeout(() => {
      setValue(newValue);
      setAnimate(false);
    }, 300);
  };

  // Hover box component with image reveal effect
  const HoverBox = ({ title, subtitle, color, icon, image }) => {
    return (
      <div className={`relative overflow-hidden rounded-lg bg-${color}-50 h-24 group`}>
        {/* Text content that slides down on hover */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-3 transform transition-transform duration-300 group-hover:translate-y-full">
          <span className={`block text-${color}-700 font-bold`}>{title}</span>
          <span className="text-gray-600">{subtitle}</span>
        </div>
        
        {/* Image that slides in from bottom on hover */}
        <div className="absolute inset-0 flex items-center justify-center transform transition-transform duration-300 translate-y-full group-hover:translate-y-0">
          <div className="absolute inset-0 bg-black/30 z-10"></div>
          <img 
            src={image} 
            alt={`${title} ${subtitle}`} 
            className="w-full h-full object-cover"
          />
          <div className="absolute z-20 text-white flex items-center justify-center">
            <div className="mr-2">{icon}</div>
            <div>
              <p className="font-bold">{title}</p>
              <p className="text-sm">{subtitle}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-8">About Us</h2>
        
        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-lg bg-gray-200 p-1">
            {sections.map((section, index) => (
              <button
                key={section.key}
                onClick={() => handleChange(index)}
                className={`px-4 py-2 rounded-md font-medium transition-all ${
                  value === index 
                    ? `bg-white text-${section.color}-600 shadow`
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                aria-controls={`organization-tabpanel-${index}`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Panels */}
        {sections.map((section, index) => (
          <TabPanel key={section.key} value={value} index={index}>
            <div 
              className={`bg-white rounded-xl shadow-lg p-6 transition-opacity duration-500 ${animate ? 'opacity-0' : 'opacity-100'}`}
            >
              <div className="flex flex-col md:flex-row">
                {/* Content */}
                <div className="md:w-1/2 pr-0 md:pr-6 mb-6 md:mb-0">
                  <h3 className={`text-2xl font-bold mb-4 text-${section.color}-700`}>
                    {section.title}
                  </h3>
                  
                  {section.key === 'values' ? (
                    <div className="space-y-4">
                      {section.content.map((value, idx) => (
                        <div key={idx} className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="bg-gray-100 p-2 rounded-full">
                            {value.icon}
                          </div>
                          <div>
                            <h4 className="font-bold text-lg text-gray-800">{value.name}</h4>
                            <p className="text-gray-600">{value.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <div className={`p-4 border-l-4 border-${section.color}-500 bg-gray-50 rounded mb-6`}>
                        <p className="text-lg text-gray-700 leading-relaxed">{section.content}</p>
                      </div>

                      {section.key === 'mission' && (
                        <div className="grid grid-cols-2 gap-3">
                          {section.boxes.map((box, idx) => (
                            <HoverBox key={idx} {...box} />
                          ))}
                        </div>
                      )}

                      {section.key === 'vision' && (
                        <div className="flex items-center justify-center space-x-4 my-4">
                          <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center border-4 border-blue-200">
                            <span className="text-blue-700 font-bold">Europe</span>
                          </div>
                          <div className="w-16 h-3 bg-gradient-to-r from-blue-500 to-orange-500 rounded"></div>
                          <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center border-4 border-orange-200">
                            <span className="text-orange-700 font-bold">India</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Image */}
                <div className="md:w-1/2">
                  <div className="relative rounded-lg overflow-hidden shadow-md h-64">
                    <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/60 to-transparent z-10 flex items-end">
                      <div className="p-4 text-white">
                        <h4 className="text-xl font-bold">{section.title}</h4>
                        <p className="text-sm opacity-90">Bringing Europe and India together</p>
                      </div>
                    </div>
                    <img
                      src={section.image}
                      alt={section.title}
                      className="object-cover w-full h-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        ))}
      </div>
    </div>
  );
}

export default OrganizationTabs;